import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { WAITLIST_ENDPOINT, WAITLIST_COUNT_ENDPOINT } from "@/lib/config";
import { validateIndianMobile } from "@/lib/validators";

export default function WaitlistForm() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [count, setCount] = useState(431);
  const [phoneErr, setPhoneErr] = useState("");

  useEffect(() => {
    fetch(WAITLIST_COUNT_ENDPOINT)
      .then((r) => r.json())
      .then((d) => typeof d.count === "number" && setCount(d.count))
      .catch(() => {});
  }, []);

  const phoneCheck = validateIndianMobile(phone);
  const valid =
    name.trim().length > 0 && phone.length === 10 && consent && phoneCheck.ok;

  const onPhone = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
    setPhone(digits);
    // Only surface an inline error once the user has typed a full 10 digits
    // to avoid nagging them mid-entry.
    if (digits.length === 10) {
      const v = validateIndianMobile(digits);
      setPhoneErr(v.ok ? "" : v.msg);
    } else {
      setPhoneErr("");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || loading) return;
    if (!phoneCheck.ok) {
      setPhoneErr(phoneCheck.msg);
      return;
    }
    setLoading(true);
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 12000);
    try {
      const res = await fetch(WAITLIST_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), whatsapp: phone, consent }),
        signal: ctrl.signal,
      });
      if (res.ok) {
        setDone(true);
        try { sessionStorage.setItem("evove_waitlist_done", "1"); } catch (_) {}
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(
          data?.detail?.[0]?.msg || "Something went wrong. Please try again.",
        );
      }
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      clearTimeout(timer);
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-[380px]" data-testid="waitlist-container">
      <AnimatePresence mode="wait">
        {done ? (
          <motion.p
            key="done"
            data-testid="waitlist-success"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="text-[0.95rem] leading-relaxed"
            style={{ color: "var(--paper)" }}
          >
            You&apos;re on the list. We&apos;ll message you on WhatsApp before
            launch.
          </motion.p>
        ) : (
          <motion.form
            key="form"
            onSubmit={submit}
            data-testid="waitlist-form"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col gap-4 md:gap-5"
          >
            <input
              type="text"
              className="field"
              placeholder="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="waitlist-name-input"
              autoComplete="name"
            />

            <div className="flex items-center gap-2 border-b border-[rgba(27,23,53,0.28)] focus-within:border-[var(--paper)] transition-colors">
              <span
                className="text-[0.95rem] pb-[10px] pt-[10px] pr-1 select-none"
                style={{ color: "var(--paper-dim)" }}
                data-testid="waitlist-phone-prefix"
              >
                +91
              </span>
              <input
                type="tel"
                inputMode="numeric"
                className="field !border-b-0 flex-1"
                placeholder="WhatsApp number"
                value={phone}
                onChange={onPhone}
                data-testid="waitlist-phone-input"
                autoComplete="tel-national"
              />
            </div>
            {phoneErr && (
              <p
                className="-mt-3 text-[0.72rem] leading-tight"
                style={{ color: "#c04a4a" }}
                data-testid="waitlist-phone-error"
              >
                {phoneErr}
              </p>
            )}

            <label
              className="flex items-start gap-3 cursor-pointer group"
              data-testid="waitlist-consent-label"
            >
              <input
                type="checkbox"
                className="sr-only"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                data-testid="waitlist-consent-checkbox"
              />
              <span className="checkbox-box" data-checked={consent}>
                {consent && (
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path
                      d="M1 5l2.5 2.5L9 1.5"
                      stroke="#eceaf6"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </span>
              <span
                className="text-[0.74rem] leading-relaxed"
                style={{ color: "var(--paper-dim)" }}
              >
                I agree to receive updates from evove on WhatsApp.{" "}
                <Link
                  to="/privacy"
                  className="link-underline"
                  data-testid="privacy-policy-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  Privacy Policy
                </Link>
              </span>
            </label>

            <button
              type="submit"
              className="cta self-start mt-1"
              disabled={!valid || loading}
              data-testid="waitlist-submit-button"
            >
              {loading ? "Joining\u2026" : "Join the waitlist"}
            </button>

            <p
              className="text-[0.62rem] track-sm uppercase mt-1"
              style={{ color: "var(--paper-dim)" }}
              data-testid="waitlist-count"
            >
              {count.toLocaleString()} People Already Joined
            </p>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
}
